import { format } from 'date-fns';
import { BarChart3, CircleCheck, Clock3, UsersRound } from 'lucide-react';
import type { AccountApplyStatistics, AccountListQuery } from '@/api/modules/business/accountApply';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { RequestErrorState } from '@/components/RequestErrorState';

interface AccountApplicationStatisticsProps {
  createTimes?: AccountListQuery['createTimes'];
  statistics?: AccountApplyStatistics;
  loading?: boolean;
  errorState?: { message: string; onRetry: () => void };
}

const statisticItems = [
  { key: 'totalApplySize', label: '申请总数', icon: BarChart3 },
  { key: 'auditingCount', label: '审核中', icon: Clock3 },
  { key: 'successCount', label: '下户成功', icon: CircleCheck },
  { key: 'failureCount', label: '下户失败', icon: UsersRound },
] as const;

/** 三种状态共用卡片行高，错误区域随响应式行数占位，避免下方列表跳动 */
export function AccountApplicationStatistics({
  createTimes,
  statistics,
  loading = false,
  errorState,
}: AccountApplicationStatisticsProps) {
  const periodText =
    createTimes?.beginTime && createTimes.endTime
      ? `${createTimes.beginTime} ~ ${createTimes.endTime}`
      : `截至 ${format(new Date(), 'yyyy-MM-dd')}`;

  return (
    <section className="@container" aria-label="开户申请统计" aria-busy={loading}>
      <div className="grid auto-rows-[minmax(9.375rem,auto)] grid-cols-1 gap-4 @xl:grid-cols-2 @5xl:grid-cols-4">
        {errorState && !loading ? (
          <Card className="col-span-full row-span-4 min-h-0 gap-0 overflow-hidden py-0 @xl:row-span-2 @5xl:row-span-1">
            {/** 局部失败文案与列表一致，具体错误分类仍保留在请求处理层 */}
            <RequestErrorState
              className="min-h-0 flex-1 py-4 md:py-4"
              onRetry={errorState.onRetry}
            />
          </Card>
        ) : (
          statisticItems.map(({ key, label, icon: Icon }) => (
            <Card
              key={key}
              className="min-w-0 gap-4 py-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <CardHeader className="px-4">
                <div className="flex items-center gap-2.5">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  <CardTitle className="text-sm">{label}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 items-end px-4">
                {loading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <span className="mt-auto block min-h-8 whitespace-nowrap text-2xl leading-8 font-semibold tabular-nums">
                    <span className="leading-none">
                      {statistics?.[key]?.toLocaleString('en-US') ?? '—'}
                    </span>
                  </span>
                )}
              </CardContent>
              <CardFooter className="px-4 text-xs text-muted-foreground">
                {loading ? <Skeleton className="h-4 w-28" /> : periodText}
              </CardFooter>
            </Card>
          ))
        )}
      </div>
    </section>
  );
}
